import { computed } from 'vue'
import useURLStore from 'src/store/useURLStore'
import useContextStore from 'src/store/useContextStore'
import type {
  SectionMenuItem,
} from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/SectionMenu/types'
import type { ContextStore } from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/index/types'
import type { Lpu } from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_types'
import tStatic from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_functions/tStatic'
import { PHARMACY_LPU_TYPE_ID } from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuDoctorsList/constants'
import shouldHideDoctorsRating from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuRating/functions/shouldHideDoctorsRating'
import unwrapGosPhones from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuPhonebook/functions/unwrapGosPhones'
import getSectionMenuPricesCount from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/SectionMenu/functions/getSectionMenuPricesCount'
import isSectionMenuPricesItemVisible from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/SectionMenu/functions/isSectionMenuPricesItemVisible'
import getSectionMenuPromotionsCount from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/SectionMenu/functions/getSectionMenuPromotionsCount'
import isSectionMenuPromotionsVisible from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/SectionMenu/functions/isSectionMenuPromotionsVisible'
import { SECTION_MENU_BLOCK_ID } from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/SectionMenu/constants'

const numberFormatter = new Intl.NumberFormat('ru-RU')

export default function useSectionMenuItems(props: {
  lpu: Lpu
  synonymPricesCount?: number
}) {
  const { reverseFull } = useURLStore()
  const { context }: ContextStore = useContextStore()

  const items = computed(() => {
    const menuItems: SectionMenuItem[] = []
    const synonymPricesCount = props.synonymPricesCount ?? context.synonym_prices_count

    if (
      props.lpu.drcnt
      && props.lpu.lputype.id !== PHARMACY_LPU_TYPE_ID
      && context.has_workplaces !== false
    ) {
      menuItems.push(
        {
          id: 'doctors',
          title: tStatic('Врачи'),
          count: numberFormatter.format(props.lpu.drcnt),
          href: '#doctors',
        },
      )
    }

    if (props.lpu.appointment_on && props.lpu.has_slots) {
      menuItems.push({
        id: 'schedule',
        title: tStatic('График врачей'),
        href: '#schedule',
      })
    }

    const hideDoctorsRating = shouldHideDoctorsRating(
      context.request.feature_flags.change_calc_rating_on_laboratory,
      props.lpu.lputype.id,
      Boolean(context.request.user.is_staff),
    )

    if (
      !props.lpu.closed
      && (
        Boolean(context.has_some_rates_rating)
        || (!hideDoctorsRating && Boolean(context.has_some_doctors_rating))
        || Boolean(props.lpu.has_fraud_mark)
        || Boolean(context.request.user.is_staff)
      )
    ) {
      menuItems.push({
        id: 'rating',
        title: tStatic('Рейтинг'),
        href: '#rating',
      })
    }

    if (isSectionMenuPricesItemVisible(props.lpu, synonymPricesCount)) {
      const lpuPriceUrl = reverseFull({
        name: 'lpu_price',
        args: [
          props.lpu.town.translit,
          props.lpu.translit,
        ],
      })
      const pricesCount = getSectionMenuPricesCount(props.lpu, synonymPricesCount)

      menuItems.push({
        id: 'prices',
        title: tStatic('Цены'),
        count: numberFormatter.format(pricesCount),
        href: '#prices',
        redirectHref: `${lpuPriceUrl}#prices`,
      })
    }

    menuItems.push({
      id: 'otzivi',
      title: tStatic('Отзывы'),
      count: numberFormatter.format(props.lpu.allrates),
      href: '#otzivi',
    })

    const isMobile = context.flavour === 'mobile'
    const isStaff = Boolean(context.request.user.is_staff)
    const promotionsCount = getSectionMenuPromotionsCount(
      context.promotions,
      context.promotions_count,
    )

    if (isSectionMenuPromotionsVisible({
      promotions: context.promotions,
      promotionsCount: context.promotions_count,
      isMobile,
      isStaff,
    })) {
      menuItems.push({
        id: 'akcii',
        title: tStatic('Акции'),
        count: promotionsCount
          ? numberFormatter.format(promotionsCount)
          : undefined,
        href: '#akcii',
      })
    }

    if (props.lpu.lpu_documents?.some(document => (
      Boolean(document.img_l?.url || document.img_s?.url)
    ))) {
      menuItems.push({
        id: 'documents',
        title: tStatic('Документы'),
        href: '#documents',
      })
    }

    if (context.related_lpus?.length) {
      menuItems.push({
        id: 'related-lpus',
        title: tStatic('Связанные клиники'),
        href: '#related-lpus',
      })
    }

    if (unwrapGosPhones(context.gos_phones).length && !props.lpu.paid) {
      menuItems.push({
        id: 'phonebook',
        title: tStatic('Телефонный справочник'),
        href: '#phonebook',
      })
    }

    if (props.lpu.lpuinterview?.text) {
      menuItems.push({
        id: 'interview',
        title: tStatic('Интервью с руководителем'),
        href: '#interview',
      })
    }

    menuItems.push({
      id: 'information',
      title: tStatic('Информация'),
      href: '#information',
    })

    const { ff_lpu_branding_page_v4 } = context.request.feature_flags
    const blockOrder = context.lpu_branding?.block_order

    if (!ff_lpu_branding_page_v4 || !blockOrder?.length) {
      return menuItems
    }

    const orderByBlockId = new Map(
      blockOrder.map((blockId, index) => [blockId, index]),
    )

    return [...menuItems].sort((left, right) => {
      const leftBlockId = SECTION_MENU_BLOCK_ID[left.id]
      const rightBlockId = SECTION_MENU_BLOCK_ID[right.id]
      const leftOrder = leftBlockId ? orderByBlockId.get(leftBlockId) : undefined
      const rightOrder = rightBlockId ? orderByBlockId.get(rightBlockId) : undefined

      if (leftOrder == null && rightOrder == null) {
        return 0
      }

      if (leftOrder == null) {
        return 1
      }

      if (rightOrder == null) {
        return -1
      }

      return leftOrder - rightOrder
    })
  })

  return {
    items,
  }
}
